/**
 * Frame-by-Frame Renderer
 * 
 * Renders each frame of the timeline to an OffscreenCanvas for export.
 * Handles video compositing and image rendering.
 */

import { isImageAsset, clamp } from '../store';

/**
 * Timeline Renderer for export
 */
export class TimelineRenderer {
  constructor(options = {}) {
    this.width = options.width || 1920;
    this.height = options.height || 1080;
    this.fps = options.fps || 30;
    
    this.canvas = null;
    this.ctx = null;
    
    // Media cache: assetId -> HTMLVideoElement | HTMLImageElement
    this.mediaCache = new Map();
    
    // Preloaded state
    this.isPreloaded = false;
  }

  /**
   * Initialize the renderer with an OffscreenCanvas
   */
  init() {
    this.canvas = new OffscreenCanvas(this.width, this.height);
    this.ctx = this.canvas.getContext('2d');
    return this;
  }

  /**
   * Preload all assets needed for rendering
   * @param {Object} assets - Asset map from timeline store
   * @param {Function} onProgress - Progress callback (0-1)
   */
  async preloadAssets(assets, onProgress = () => {}) {
    const assetList = Object.values(assets);
    let loaded = 0;

    const loadPromises = assetList.map(async (asset) => {
      try {
        if (isImageAsset(asset)) {
          await this.loadImage(asset);
        } else if (asset.type === 'video') {
          await this.loadVideo(asset);
        }
        // Audio-only assets don't need visual preloading
      } catch (e) {
        console.warn(`Failed to preload asset ${asset.id}:`, e);
      }
      
      loaded++;
      onProgress(loaded / assetList.length);
    });

    await Promise.all(loadPromises);
    this.isPreloaded = true;
  }

  /**
   * Load an image asset
   */
  async loadImage(asset) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        this.mediaCache.set(asset.id, img);
        resolve(img);
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${asset.url}`));
      };
      
      img.src = asset.url;
    });
  }

  /**
   * Load a video asset and prepare it for frame extraction
   */
  async loadVideo(asset) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      
      video.onloadeddata = () => {
        this.mediaCache.set(asset.id, video);
        resolve(video);
      };
      
      video.onerror = () => {
        reject(new Error(`Failed to load video: ${asset.url}`));
      };
      
      video.src = asset.url;
      video.load();
    });
  }

  /**
   * Get the top video/image clip at a given time
   * @param {Array} clips - All clips
   * @param {Array} tracks - All tracks
   * @param {number} time - Time in seconds
   */
  getTopVideoClipAt(clips, tracks, time) {
    // Get video tracks sorted by priority (first track = highest priority)
    const videoTracks = tracks.filter(t => t.type === 'video');
    
    for (const track of videoTracks) {
      const clip = clips.find(c => 
        c.trackId === track.id &&
        time >= c.start &&
        time < c.start + c.duration
      );
      if (clip) return clip;
    }
    
    return null;
  }

  /**
   * Seek a video element to a specific time and wait for the frame
   */
  async seekVideo(video, time) {
    return new Promise((resolve) => {
      // If already at the right time, resolve immediately
      if (Math.abs(video.currentTime - time) < 0.01) {
        resolve();
        return;
      }

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        // Small delay to ensure frame is ready
        requestAnimationFrame(() => resolve());
      };

      video.addEventListener('seeked', onSeeked);
      video.currentTime = time;
      
      // Timeout fallback
      setTimeout(() => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      }, 1000);
    });
  }

  /**
   * Render a single frame at the given time
   * @param {number} time - Time in seconds
   * @param {Object} timeline - Timeline data (tracks, clips, assets)
   * @returns {VideoFrame} The rendered frame
   */
  async renderFrame(time, timeline) {
    const { tracks, clips, assets } = timeline;
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    // Clear canvas to black
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    // Get the top video/image clip at this time
    const clip = this.getTopVideoClipAt(clips, tracks, time);
    
    if (!clip) {
      // No clip at this time - return black frame
      return new VideoFrame(this.canvas, {
        timestamp: Math.round(time * 1_000_000),
        duration: Math.round((1 / this.fps) * 1_000_000),
      });
    }

    const asset = assets[clip.assetId];
    if (!asset) {
      return new VideoFrame(this.canvas, {
        timestamp: Math.round(time * 1_000_000),
        duration: Math.round((1 / this.fps) * 1_000_000),
      });
    }

    const media = this.mediaCache.get(asset.id);
    if (!media) {
      console.warn(`Media not found in cache: ${asset.id}`);
      return new VideoFrame(this.canvas, {
        timestamp: Math.round(time * 1_000_000),
        duration: Math.round((1 / this.fps) * 1_000_000),
      });
    }

    // Calculate asset time
    const assetTime = clip.in + (time - clip.start);

    if (isImageAsset(asset)) {
      this.drawMedia(media, media.naturalWidth, media.naturalHeight, clip);
    } else {
      const clampedAssetTime = clamp(assetTime, 0, (asset.duration || Infinity) - 0.001);
      await this.seekVideo(media, clampedAssetTime);
      
      if (media.videoWidth > 0 && media.videoHeight > 0) {
        this.drawMedia(media, media.videoWidth, media.videoHeight, clip);
      }
    }

    // Create VideoFrame from canvas
    return new VideoFrame(this.canvas, {
      timestamp: Math.round(time * 1_000_000),
      duration: Math.round((1 / this.fps) * 1_000_000),
    });
  }

  /**
   * Draw media to canvas using clip transform properties when present,
   * falling back to centered contain-fit.
   */
  drawMedia(media, sourceWidth, sourceHeight, clip = null) {
    const w = this.width;
    const h = this.height;

    const hasTransform = clip && (clip.mediaWidth != null || clip.mediaHeight != null || clip.mediaPosX != null || clip.mediaPosY != null);

    if (hasTransform) {
      const dw = clip.mediaWidth ?? w;
      const dh = clip.mediaHeight ?? h;
      const dx = (clip.mediaPosX ?? 0) + (w - dw) / 2;
      const dy = (clip.mediaPosY ?? 0) + (h - dh) / 2;
      this.ctx.drawImage(media, dx, dy, dw, dh);
    } else {
      const scale = Math.min(w / sourceWidth, h / sourceHeight);
      const dw = sourceWidth * scale;
      const dh = sourceHeight * scale;
      const dx = (w - dw) / 2;
      const dy = (h - dh) / 2;
      this.ctx.drawImage(media, dx, dy, dw, dh);
    }
  }

  /**
   * Calculate total timeline duration
   */
  getTimelineDuration(clips) {
    if (clips.length === 0) return 0;
    return Math.max(...clips.map(c => c.start + c.duration));
  }

  /**
   * Clean up resources
   */
  destroy() {
    // Pause and clean up video elements
    for (const media of this.mediaCache.values()) {
      if (media instanceof HTMLVideoElement) {
        media.pause();
        media.src = '';
        media.load();
      }
    }
    this.mediaCache.clear();
    this.canvas = null;
    this.ctx = null;
  }
}

/**
 * Create and initialize a new timeline renderer
 */
export function createRenderer(options) {
  const renderer = new TimelineRenderer(options);
  renderer.init();
  return renderer;
}

